<script>
  import Text from '@components/general/text.svelte'
  import Tag from '@components/general/tag.svelte'
  import Image from '@components/general/image.svelte'
  import EnrollmentButton from '@components/live_sessions/enrollment-button.svelte'
  import { format } from 'date-fns'
  import { formatTime } from '@lib/utils/date'
  import { Calendar, Clock, Video } from 'lucide-svelte'
  import { colors } from '@lib/utils/colors'
  import { cn } from '@lib/utils'
  import { router } from '@inertiajs/svelte'

  let { session } = $props()

  let color = $derived(session?.color || 'gray')
</script>

<section class={cn('relative rounded-lg overflow-hidden shadow-sm pt-18', colors[color]?.gradient)}>

  <!-- Date indicator -->
  <div class="absolute top-4 right-4 px-4 py-1 bg-white rounded-md flex flex-col gap-0.5 items-center justify-center shadow-sm">
    <Text as="span" class="text-3xl tracking-wide font-serif text-gray-700">{format(session.start_time, 'd')}</Text>
    <Text variant="label" as="span" class="uppercase font-light">{format(session.start_time, 'MMM')}</Text>
  </div>

  <!-- Cover image or decorative icon -->
  {#if session.cover_image}
    <Image src={session.cover_image.thumb_url} alt={session.cover_image.name} class="absolute bottom-0 right-6 z-10 w-[clamp(6rem,15vw,10rem)] h-[clamp(6rem,14vw,9.5rem)] rounded-t-sm shadow-[0_2px_6px_4px_rgba(24,15,15,0.12)] object-cover" />
  {:else}
    <div class="absolute -bottom-4 -right-4 rotate-[-8deg] opacity-[0.07]">
      <Video size={180} strokeWidth={0.75} />
    </div>
  {/if}

  <!-- Content -->
  <div class="p-6 pt-10 pb-6 flex flex-col gap-2 max-w-[70%]">

    <!-- Event name -->
    <Text variant="title" as="h1" text={session.event_name} class="sm:text-3xl" />

    <!-- A container for tags (including the session name as a tag) -->
    <div class="flex flex-wrap gap-1">
    
      <!-- Session tag -->
      <Tag tag={{ name: session.name, color }} size="xs" class="rounded-md px-[6px]" variant="faint" />

      <!-- Loop through tags and add them to the container -->
      {#each session.tags as tag (tag.id)}
        <Tag {tag} size="xs" class="rounded-md px-[6px]" variant="faint" onSelect={() => router.visit(`/live-sessions?tag=${tag.id}`)} />
      {/each}
    </div>

    <!-- Date and time -->
    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">

      <!-- Date -->
      <Text variant="label" as="span" class="flex items-center gap-1.5 text-gray-600">
        <Calendar size={14} class="text-gray-500" />
        {format(session.start_time, 'EEE, d MMMM yyyy')}
      </Text>
      
      <!-- Time -->
      <Text variant="label" as="span" class="flex items-center gap-1.5 text-gray-600">
        <Clock size={14} class="text-gray-500" />
        {formatTime(session.start_time, { mode: '12h' })}
      </Text>

      <!-- Presenter -->
      {#if session.contributor}
        <div class="flex items-center gap-2">
          <Image src={session.contributor.image} alt={session.contributor.name} text={session.contributor.name?.charAt(0)} class="w-6 h-6 rounded-full border border-gray-200/70 bg-gray-100 text-xs text-gray-400" />
          <Text variant="label" as="span" class="text-gray-500">{session.contributor.name}</Text>
        </div>
      {/if}
      
    </div>

    <!-- Action buttons -->
    <div class="mt-2">
      <EnrollmentButton {session} />
    </div>

  </div>

</section>
